import { useState } from "react";
import { supabase } from "../lib/supabase";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { CheckCircle2, XCircle, Mail, Loader2, AlertCircle } from "lucide-react";

export function EmailTest() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{
    type: "success" | "error" | "info";
    message: string;
    details?: string;
  } | null>(null);

  const testMagicLink = async () => {
    if (!email) {
      setResult({
        type: "error",
        message: "Please enter an email address",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setResult({
        type: "error",
        message: "Please enter a valid email address",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      console.log("🧪 EMAIL TEST - Sending magic link to:", email);
      
      const { data, error } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        console.error("❌ Magic link error:", error);
        setResult({
          type: "error",
          message: "Failed to send magic link",
          details: error.message,
        });
      } else {
        console.log("✅ Magic link sent successfully:", data);
        setResult({
          type: "success",
          message: "Magic link sent successfully!",
          details: "Check your email inbox (and spam folder) for the login link.",
        });
      }
    } catch (err) {
      console.error("❌ Exception sending magic link:", err);
      setResult({
        type: "error",
        message: "Unexpected error occurred",
        details: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const testSignUp = async () => {
    if (!email) {
      setResult({
        type: "error",
        message: "Please enter an email address",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setResult({
        type: "error",
        message: "Please enter a valid email address",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      console.log("🧪 EMAIL TEST - Testing signup email to:", email);
      
      const { data, error } = await supabase.auth.signUp({
        email: email,
        password: "test-password-123", // Temporary password for testing
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) {
        console.error("❌ Signup error:", error);
        setResult({
          type: "error",
          message: "Failed to send signup email",
          details: error.message,
        });
      } else {
        console.log("✅ Signup email sent successfully:", data);
        setResult({
          type: "success",
          message: "Signup email sent successfully!",
          details: "Check your email inbox (and spam folder) for the confirmation link.",
        });
      }
    } catch (err) {
      console.error("❌ Exception sending signup email:", err);
      setResult({
        type: "error",
        message: "Unexpected error occurred",
        details: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background gradient-mesh py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Email Integration Test</h1>
          <p className="text-muted-foreground">
            Test SendGrid SMTP integration for Supabase authentication emails
          </p>
        </div>

        <Card className="p-6 mb-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Test Email Address</Label>
              <Input
                id="test-email"
                type="email"
                placeholder="your-email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Enter your email address to receive test emails
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={testMagicLink}
                disabled={loading || !email}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Test Magic Link
                  </>
                )}
              </Button>

              <Button
                onClick={testSignUp}
                disabled={loading || !email}
                variant="outline"
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Test Signup Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card>

        {result && (
          <Alert
            className={
              result.type === "success"
                ? "border-green-500 bg-green-50 text-green-900"
                : result.type === "error"
                ? "border-red-500 bg-red-50 text-red-900"
                : ""
            }
          >
            <div className="flex items-start gap-3">
              {result.type === "success" ? (
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              ) : result.type === "error" ? (
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              )}
              <div className="flex-1">
                <h3 className="font-semibold mb-1">{result.message}</h3>
                {result.details && (
                  <AlertDescription className="text-sm">
                    {result.details}
                  </AlertDescription>
                )}
              </div>
            </div>
          </Alert>
        )}

        <Card className="p-6 mt-6 bg-blue-50 border-blue-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-blue-600" />
            Testing Instructions
          </h3>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">1.</span>
              <span>
                <strong>Configure SendGrid in Supabase first:</strong> Go to your
                Supabase Dashboard → Authentication → Settings → SMTP Settings and
                enter your SendGrid credentials.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">2.</span>
              <span>
                <strong>Enter your email address</strong> in the field above (use an
                email you can access).
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">3.</span>
              <span>
                <strong>Click "Test Magic Link"</strong> to send a passwordless login
                email via SendGrid.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">4.</span>
              <span>
                <strong>Check your inbox</strong> (and spam folder) for an email from
                team@cannabias.co.uk.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">5.</span>
              <span>
                <strong>Verify sender information:</strong> The email should show
                "CannaBias &lt;team@cannabias.co.uk&gt;" as the sender.
              </span>
            </li>
            <li className="flex gap-2">
              <span className="font-semibold text-foreground">6.</span>
              <span>
                <strong>Check email headers</strong> (in Gmail: three dots → Show
                original) to verify SPF and DKIM pass.
              </span>
            </li>
          </ol>
        </Card>

        <Card className="p-6 mt-6">
          <h3 className="font-semibold mb-3">Expected Email Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Sender Name:</span>
              <span className="font-medium">CannaBias</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Sender Email:</span>
              <span className="font-medium">team@cannabias.co.uk</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">SMTP Host:</span>
              <span className="font-medium">smtp.sendgrid.net</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">SMTP Port:</span>
              <span className="font-medium">587 (TLS)</span>
            </div>
            <div className="flex justify-between py-2 border-b">
              <span className="text-muted-foreground">Expected Delivery:</span>
              <span className="font-medium">Within 30 seconds</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-muted-foreground">Outlook SafeLinks:</span>
              <span className="font-medium text-green-600 flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                Compatible
              </span>
            </div>
          </div>
        </Card>

        <Card className="p-6 mt-6 bg-amber-50 border-amber-200">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            Troubleshooting
          </h3>
          <div className="space-y-3 text-sm text-muted-foreground">
            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Email not received?
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Check spam/junk folder</li>
                <li>Verify SendGrid SMTP is enabled in Supabase</li>
                <li>Check SendGrid Activity Feed for delivery status</li>
                <li>Verify sender email is verified in SendGrid</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Error sending email?
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Verify API key has "Mail Send" permissions</li>
                <li>Check username is exactly "apikey"</li>
                <li>Verify API key is correct (starts with SG.)</li>
                <li>Check Supabase logs for errors</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-1">
                Email goes to spam?
              </h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Authenticate your domain in SendGrid</li>
                <li>Add SPF/DKIM DNS records</li>
                <li>Check email content doesn't trigger spam filters</li>
                <li>Test with mail-tester.com</li>
              </ul>
            </div>
          </div>
        </Card>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Need help? Check{" "}
            <a
              href="/#"
              onClick={(e) => {
                e.preventDefault();
                window.open("/SENDGRID_SMTP_SETUP.md", "_blank");
              }}
              className="text-primary hover:underline"
            >
              SendGrid SMTP Setup Guide
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
